import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ApiService } from '@core/services/api';
import { PaymentActivationStatus } from '@core/services/api/interfaces/payment-activation';

/**
 * "Precisa de atenção" para as entradas de Configurações — Recebimentos,
 * Nota fiscal e Cobranças (dono, 23/09/2026).
 *
 * ⚠️ **Inteligência artificial fica de fora, e é decisão do dono, não
 * esquecimento.** Nos três domínios acima, "desligado" é pendência de
 * CONFIGURAÇÃO (falta certificado, falta provedor, falta régua). Em IA,
 * "desligado" é ausência de CONTRATAÇÃO: é um recurso pago que nasce
 * desligado para toda empresa nova, e marcar isso como pendência criaria um
 * alerta permanente e falso em toda empresa que nunca quis IA — a análise
 * que levou a essa decisão está em `PremiumSettingsService`/`PremiumSettings`
 * (`scan_mode`/`governance_ai`/`chat_ai`, todos desligados por padrão). Não
 * estenda esta marca para IA sem alinhar de novo com o dono.
 *
 * Cada domínio é UMA chamada leve e cacheada (`shareReplay(1)`, uma rodada
 * de requisições por sessão, não uma por consumidor):
 * - `payment-activation` já é a configuração DA EMPRESA (a mesma que a tela
 *   de Recebimentos usa para se desenhar) — nenhuma cobrança pesada por
 *   conta a receber.
 * - `configuration/fiscal-rps` é a configuração DA EMPRESA (a mesma que a
 *   tela de Nota fiscal usa para se desenhar) — não é o
 *   `check-fiscal-rps`, que é por CONTA A RECEBER e pesado.
 * - `template.list({ per_page: 1 })` lê só `meta.total`, sem carregar o
 *   `AccountReceivableService` inteiro (que hoje busca até 9999 templates
 *   para a régua de cobrança).
 *
 * Erro de rede vira "não precisa de atenção", nunca o contrário — estado
 * desconhecido não pode virar pendência inventada (mesmo princípio do
 * `ativacaoDeRecebimentos` em `accounts-receivable/list.component.ts`).
 */
@Injectable({ providedIn: 'root' })
export class ConfiguracaoAtencaoService {
  private readonly api = inject(ApiService);

  private readonly recebimentosAtualizados = new BehaviorSubject<PaymentActivationStatus | null>(null);

  /** Reutiliza a resposta já lida pela configuração; não refaz consultas fiscais ou de cobranças. */
  atualizarRecebimentos(status: PaymentActivationStatus): void {
    this.recebimentosAtualizados.next(status);
  }

  /** Descarta o estado da empresa quando o menu da sessão é desmontado. */
  limparRecebimentos(): void {
    this.recebimentosAtualizados.next(null);
  }

  readonly recebimentosStatus$: Observable<PaymentActivationStatus | null> = this.recebimentosAtualizados.pipe(
    switchMap(status => status ? of(status) : this.api.configuration.getPaymentActivation().pipe(
      catchError(() => of(null)),
    )),
    shareReplay(1),
  );

  readonly recebimentosPendente$: Observable<boolean> = this.recebimentosStatus$.pipe(
    map(status => {
      if (!status) return false;
      const contratados = [status.boleto, status.pix, status.card].filter(instrumento => instrumento?.module);
      return (status.link?.module === true && !status.link.active)
        || (contratados.length > 0 && !contratados.some(instrumento => instrumento.active));
    }),
    shareReplay(1),
  );

  readonly notaFiscalPendente$: Observable<boolean> = this.api.configuration.getFiscalRps().pipe(
    map(config => !config?.certificate),
    catchError(() => of(false)),
    shareReplay(1),
  );

  readonly cobrancaPendente$: Observable<boolean> = this.api.template.list({ per_page: 1 }).pipe(
    map(pagina => (pagina?.meta?.total ?? 0) === 0),
    catchError(() => of(false)),
    shareReplay(1),
  );
}
